import { Table, Tag, Typography, Space, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useQueryClient } from '@tanstack/react-query';
import { useWisecureMutation } from '@wisecure/api-client/hooks';
import type {
  SecurityCatalogItemResponse,
  SecurityCatalogResponse,
  SecurityFormula,
  UpdateSecurityCatalogItemDto,
} from '@wisecure/api-client';
import { queryKeys } from '@/api/query-keys';
import { neutral } from '@/theme';
import { CommitNumberInput, CommitTextInput } from './CommitInputs';
import { FORMULAS, FORMULA_LABELS, formatEuro } from './labels';

const { Text } = Typography;

type Item = SecurityCatalogItemResponse;

const PLAN_FIELDS: Record<
  SecurityFormula,
  {
    purchase: 'purchaseEssentiel' | 'purchaseConfort' | 'purchasePremium';
    sale: 'saleEssentiel' | 'saleConfort' | 'salePremium';
    variant: 'variantEssentiel' | 'variantConfort' | 'variantPremium';
  }
> = {
  ESSENTIEL: { purchase: 'purchaseEssentiel', sale: 'saleEssentiel', variant: 'variantEssentiel' },
  CONFORT: { purchase: 'purchaseConfort', sale: 'saleConfort', variant: 'variantConfort' },
  PREMIUM: { purchase: 'purchasePremium', sale: 'salePremium', variant: 'variantPremium' },
};

function useUpdateCatalogItem() {
  const queryClient = useQueryClient();
  return useWisecureMutation<Item, { sku: string; body: UpdateSecurityCatalogItemDto }>(
    (client, { sku, body }) =>
      client.dashboardSecurity.securityDashboardControllerUpdateItem({ sku, requestBody: body }),
    {
      onSuccess: (item) => {
        queryClient.setQueryData<SecurityCatalogResponse>(queryKeys.securityCatalog, (old) =>
          old ? { ...old, items: old.items.map((i) => (i.sku === item.sku ? item : i)) } : old,
        );
        message.success({ content: `${item.sku} enregistré`, key: 'security-catalog-save' });
      },
      onError: () => message.error({ content: "Impossible d'enregistrer la modification", key: 'security-catalog-save' }),
    },
  );
}

interface CatalogItemsTableProps {
  items: Item[];
  /** Options are priced with the Essentiel price only: hide the other plans. */
  singlePlan?: boolean;
}

/** Editable catalogue table: every field saves on blur / Enter, sale prices come back computed by the server. */
export function CatalogItemsTable({ items, singlePlan }: CatalogItemsTableProps) {
  const update = useUpdateCatalogItem();
  const save = (sku: string, body: UpdateSecurityCatalogItemDto) => update.mutateAsync({ sku, body });
  const plans = singlePlan ? (['ESSENTIEL'] as SecurityFormula[]) : FORMULAS;

  const planColumns: ColumnsType<Item> = plans.map((formula) => {
    const fields = PLAN_FIELDS[formula];
    return {
      title: singlePlan ? 'Prix (toutes formules)' : FORMULA_LABELS[formula],
      key: formula,
      align: 'center',
      children: [
        {
          title: 'Achat HT',
          key: `${formula}-purchase`,
          width: 120,
          render: (_, item) => (
            <CommitNumberInput
              key={`${item[fields.purchase] ?? 'none'}`}
              value={item[fields.purchase]}
              nullable
              suffix="€"
              placeholder="—"
              onCommit={(v) => save(item.sku, { [fields.purchase]: v })}
            />
          ),
        },
        {
          title: 'Vente',
          key: `${formula}-sale`,
          width: 100,
          align: 'right',
          render: (_, item) =>
            item[fields.sale] == null ? (
              <Tag style={{ marginInlineEnd: 0 }}>Hors formule</Tag>
            ) : (
              <Text strong>{formatEuro(item[fields.sale])}</Text>
            ),
        },
      ],
    };
  });

  const columns: ColumnsType<Item> = [
    {
      title: 'Réf.',
      dataIndex: 'sku',
      key: 'sku',
      width: 110,
      fixed: 'left',
      render: (sku: string) => <Text style={{ fontFamily: 'monospace', fontSize: 12 }}>{sku}</Text>,
    },
    {
      title: 'Libellé',
      key: 'label',
      width: 240,
      render: (_, item) => (
        <CommitTextInput key={item.label} value={item.label} onCommit={(v) => save(item.sku, { label: v ?? undefined })} />
      ),
    },
    ...planColumns,
    {
      title: 'Marge',
      key: 'marginPct',
      width: 100,
      render: (_, item) => (
        <CommitNumberInput
          key={item.marginPct}
          value={item.marginPct}
          max={1000}
          suffix="%"
          width={90}
          onCommit={(v) => save(item.sku, { marginPct: v ?? undefined })}
        />
      ),
    },
    {
      title: 'Pose / unité',
      key: 'installMinutes',
      width: 110,
      render: (_, item) => (
        <CommitNumberInput
          key={item.installMinutes}
          value={item.installMinutes}
          max={10000}
          precision={0}
          suffix="min"
          width={100}
          onCommit={(v) => save(item.sku, { installMinutes: v ?? undefined })}
        />
      ),
    },
    {
      title: 'Fournitures / unité',
      key: 'suppliesPrice',
      width: 110,
      render: (_, item) => (
        <CommitNumberInput
          key={item.suppliesPrice}
          value={item.suppliesPrice}
          suffix="€"
          width={90}
          onCommit={(v) => save(item.sku, { suppliesPrice: v ?? undefined })}
        />
      ),
    },
    {
      title: 'Qté min.',
      dataIndex: 'minQuantity',
      key: 'minQuantity',
      width: 80,
      align: 'right',
    },
  ];

  return (
    <Table<Item>
      columns={columns}
      dataSource={items}
      rowKey="sku"
      pagination={false}
      size="small"
      bordered
      scroll={{ x: 'max-content' }}
      expandable={{
        expandedRowRender: (item) => (
          <Space wrap size={16} style={{ padding: '4px 0' }}>
            <Text style={{ color: neutral[500], fontSize: 12 }}>Variante affichée au client :</Text>
            {plans.map((formula) => {
              const field = PLAN_FIELDS[formula].variant;
              return (
                <Space key={formula} size={6}>
                  <Text style={{ fontSize: 12 }}>{FORMULA_LABELS[formula]}</Text>
                  <CommitTextInput
                    key={item[field] ?? ''}
                    value={item[field]}
                    nullable
                    placeholder="Aucune"
                    width={260}
                    onCommit={(v) => save(item.sku, { [field]: v })}
                  />
                </Space>
              );
            })}
          </Space>
        ),
      }}
    />
  );
}
